import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, ChevronRight } from "lucide-react";

const STATUS_OPTIONS = [
  { key: "submitted", label: "Eingegangen", color: "bg-amber-100 text-amber-800" },
  { key: "in_review", label: "In Prüfung", color: "bg-blue-100 text-blue-800" },
  { key: "converted", label: "Konvertiert", color: "bg-emerald-100 text-emerald-800" },
  { key: "rejected", label: "Abgelehnt", color: "bg-slate-200 text-slate-600" },
];

const DOC_TYPE_LABEL = {
  id_card: "Personalausweis",
  passport: "Reisepass",
  hr_extract: "Handelsregisterauszug",
  residence_proof: "Wohnsitzbestätigung",
  other: "Sonstiges",
};

export default function AdminIntakes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/admin/intakes").then(({ data }) => { setItems(data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/intakes/${id}`, { status });
      toast.success("Status aktualisiert");
      load();
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
    } catch { toast.error("Fehler"); }
  };

  const saveNotes = async (id, notes) => {
    try {
      await api.patch(`/admin/intakes/${id}`, { internal_notes: notes });
      toast.success("Notizen gespeichert");
    } catch { toast.error("Fehler"); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-8" data-testid="admin-intakes">
      <h1 className="font-heading text-3xl font-bold text-[#1A2433] mb-2">Onboarding-Anfragen</h1>
      <p className="text-slate-600 mb-6">{items.length} Einreichungen.</p>

      {loading ? <div className="text-slate-500">Lade…</div> : (
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold">Kunde</th>
                <th className="px-4 py-3 font-semibold">Rechtsform</th>
                <th className="px-4 py-3 font-semibold">Branche</th>
                <th className="px-4 py-3 font-semibold">Eingang</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#1A2433]">
                      {it.user?.first_name} {it.user?.last_name}
                    </div>
                    <div className="text-xs text-slate-500">{it.user?.email}</div>
                  </td>
                  <td className="px-4 py-3">{it.data?.legal_form_target || "—"}</td>
                  <td className="px-4 py-3">{it.data?.business_sector || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{new Date(it.created_at).toLocaleDateString("de-CH")}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={it.status} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setSelected(it)} className="text-[#991B1B] hover:underline text-sm font-medium" data-testid={`intake-open-${it.id}`}>
                      Details <ChevronRight className="w-3 h-3 inline" />
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">Noch keine Anfragen.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <IntakeDetailDrawer
          item={selected}
          onClose={() => setSelected(null)}
          onStatus={(s) => updateStatus(selected.id, s)}
          onSaveNotes={(n) => saveNotes(selected.id, n)}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const opt = STATUS_OPTIONS.find((s) => s.key === status) || STATUS_OPTIONS[0];
  return <span className={`inline-block text-xs font-semibold px-2 py-1 rounded-full ${opt.color}`}>{opt.label}</span>;
}

function IntakeDetailDrawer({ item, onClose, onStatus, onSaveNotes }) {
  const [notes, setNotes] = useState(item.internal_notes || "");
  const [documents, setDocuments] = useState([]);
  const d = item.data || {};

  useEffect(() => {
    if (!item.user?.id) return;
    api.get(`/admin/users/${item.user.id}/documents`)
      .then(({ data }) => setDocuments(data))
      .catch(() => setDocuments([]));
  }, [item.user?.id]);

  const downloadDoc = async (id, filename) => {
    try {
      const res = await api.get(`/admin/documents/${id}/file`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download fehlgeschlagen"); }
  };
  return (
    <div className="fixed inset-0 z-[10001] bg-black/40 flex items-stretch justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="intake-drawer">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="mbw-overline mb-2">Anfrage</div>
            <h2 className="font-heading text-2xl font-bold text-[#1A2433]">{item.user?.first_name} {item.user?.last_name}</h2>
            <div className="text-sm text-slate-500">{item.user?.email} · {new Date(item.created_at).toLocaleString("de-CH")}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="mb-6">
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Status</label>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s.key}
                onClick={() => onStatus(s.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                  item.status === s.key ? "bg-[#1A2433] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <Section title="Vorhaben">
          <Row k="Rechtsform" v={d.legal_form_target} />
          <Row k="Firmenname" v={d.company_name_preferred} />
          <Row k="Zweck" v={d.company_purpose} />
          <Row k="Branche" v={d.business_sector} />
          <Row k="Erwarteter Umsatz" v={d.expected_revenue_chf && `CHF ${Number(d.expected_revenue_chf).toLocaleString("de-CH")}`} />
          <Row k="Mitarbeitende" v={d.expected_employees} />
        </Section>

        <Section title="Gründer:in">
          <Row k="Adresse" v={d.founder_address} />
          <Row k="Land" v={d.founder_country} />
          <Row k="Staatsangehörigkeit" v={d.nationality} />
          <Row k="Geburtsdatum" v={d.date_of_birth} />
        </Section>

        <Section title="Leistungen">
          <Row k="Interessiert an" v={(d.services_interested || []).join(", ")} />
          <Row k="Domizil benötigt" v={d.domicile_needed ? "Ja" : "Nein"} />
          <Row k="Buchhaltung benötigt" v={d.accounting_needed ? "Ja" : "Nein"} />
          <Row k="Steuerberatung benötigt" v={d.tax_advisory_needed ? "Ja" : "Nein"} />
        </Section>

        <Section title="Timing">
          <Row k="Gewünschter Start" v={d.target_start_date} />
          <Row k="Budget" v={d.budget_range} />
          <Row k="Empfohlen durch" v={d.referred_by} />
          <Row k="Notizen Kunde" v={d.notes} />
        </Section>

        <Section title="KYC / Compliance">
          <Row k="HR-Nummer" v={d.hr_number} />
          <Row k="UBO Name" v={d.ubo_name} />
          <Row k="UBO Geburtsdatum" v={d.ubo_dob} />
          <Row k="UBO Nationalität" v={d.ubo_nationality} />
          <Row k="UBO Wohnsitz" v={d.ubo_residence} />
          <Row k="UBO Beteiligung %" v={d.ubo_share_percent} />
          <Row k="Weitere UBOs" v={d.additional_ubos ? "Ja" : null} />
          <Row k="Mittelherkunft" v={(d.funds_origin || []).join(", ")} />
          <Row k="Mittel sonstiges" v={d.funds_origin_other} />
          <Row k="Mittel-Beschreibung" v={d.funds_description} />
          <Row k="Zweck Geschäftsbeziehung" v={(d.business_purpose_kyc || []).join(", ")} />
          <Row k="Zweck sonstiges" v={d.business_purpose_other} />
          <Row k="PEP" v={d.is_pep ? `Ja — ${d.pep_details || ''}` : "Nein"} />
          <Row k="Sanktionen bestätigt" v={d.sanctions_confirmed ? "Ja" : "Nein"} />
        </Section>

        <div className="mb-6 border-t border-slate-100 pt-6" data-testid="intake-drawer-documents">
          <h3 className="font-heading font-semibold text-[#1A2433] mb-3">
            Hochgeladene Dokumente
            <span className="ml-2 text-xs font-normal text-slate-500">({documents.length})</span>
          </h3>
          {documents.length === 0 ? (
            <div className="text-sm text-slate-500 italic">Noch keine Dokumente hochgeladen.</div>
          ) : (
            <div className="space-y-1.5">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                  data-testid={`admin-doc-${doc.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="truncate text-[#1A2433] font-medium">
                        {DOC_TYPE_LABEL[doc.doc_type] || doc.doc_type}
                        {doc.label && doc.label !== DOC_TYPE_LABEL[doc.doc_type] && (
                          <span className="ml-1 text-slate-500 font-normal">— {doc.label}</span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {doc.filename} · {(doc.size / 1024).toFixed(0)} KB · {new Date(doc.created_at).toLocaleString("de-CH")}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => downloadDoc(doc.id, doc.filename)}
                    className="p-1.5 text-slate-500 hover:text-[#991B1B] hover:bg-white rounded transition-colors flex-shrink-0"
                    title="Herunterladen"
                    data-testid={`admin-doc-download-${doc.id}`}
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8">
          <label className="block text-sm font-semibold text-[#1A2433] mb-2">Interne Notizen</label>
          <textarea
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notizen für das Team…"
            className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#991B1B]"
            data-testid="intake-notes"
          />
          <button onClick={() => onSaveNotes(notes)} className="btn-primary mt-3" data-testid="intake-save-notes">
            Notizen speichern
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-6 border-t border-slate-100 pt-6">
      <h3 className="font-heading font-semibold text-[#1A2433] mb-3">{title}</h3>
      <dl className="space-y-2 text-sm">{children}</dl>
    </div>
  );
}
function Row({ k, v }) {
  if (!v && v !== 0) return null;
  return (
    <div className="grid grid-cols-3 gap-3">
      <dt className="text-slate-500">{k}</dt>
      <dd className="col-span-2 text-[#1A2433] break-words">{String(v)}</dd>
    </div>
  );
}
